import React, { useState, useEffect } from "react";
import { FileBarChart2, ShieldCheck, Download, Lock, Mail, Unlock, BadgeCheck, Loader2, CheckCircle2, XCircle, Circle, RotateCcw, History, Trash2, AlertTriangle, FolderArchive } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL } from "@/lib/apiClient";
import { useGate } from "@/context/GateContext";
import { usePaywall } from "@/components/Paywall";
import { PinPad } from "@/components/PinPad";
import ShareModal from "@/components/ShareModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { JusticeThinking, REPORT_STAGE_MESSAGES } from "@/components/Justice";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const REPORT_LABELS = {
  master: "Master Case Report", focused: "Focused Report", timeline: "Timeline Report",
  communication: "Communication Report", financial: "Financial Report",
  consistency: "Court-Order Consistency Report", index: "Evidence Index",
  professional: "Professional Review Package",
  calendar_log: "Calendar Transmission Log", exhibit_record: "Exhibit Record",
  clinical_summary: "Clinical, Health & Behavioral Summary", fact_metrics: "Fact Metrics Report",
  memo: "Memorandum of Documented Facts", objective: "Objective-Driven Case Report",
  evidence_index: "Organize My Evidence (GAL Index)", full_court_prep: "Full Court Prep Package",
  discovery_certificate: "Discovery Response Certificate",
};

export default function Reports({ user, incidents, matterId, trialing, onSetupPin, reloadAudit }) {
  const { gate } = useGate();
  const { openPaywall } = usePaywall();
  const [shareOpen, setShareOpen] = useState(false);
  const [trialGateOpen, setTrialGateOpen] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestHtml, setDigestHtml] = useState("");
  const openDigest = async () => {
    try { const { data } = await api.get("/cron/digest-preview"); setDigestHtml(data.html); setDigestOpen(true); }
    catch { toast.error("Could not load preview"); }
  };
  const [selected, setSelected] = useState([]);
  const [caseNumber, setCaseNumber] = useState("");
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [pinError, setPinError] = useState("");
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [history, setHistory] = useState([]);
  const [delJob, setDelJob] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  const deleteReport = async () => {
    setDelBusy(true);
    try {
      await api.delete(`/reports/jobs/${delJob.job_id}`);
      toast.success("Report permanently deleted");
      if (jobId === delJob.job_id) { setJob(null); setJobId(null); }
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not delete report"); }
    finally { setDelBusy(false); setDelJob(null); }
  };
  const [exportMode, setExportMode] = useState("judicial");
  const [includeComms, setIncludeComms] = useState(true);
  const [reportType, setReportType] = useState("master");
  const [contentScope, setContentScope] = useState("both");
  const [layoutMode, setLayoutMode] = useState("court_ready");
  const [archives, setArchives] = useState([]);
  const [archiveIds, setArchiveIds] = useState([]);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState("");

  // Case Objective (for the Objective-Driven Report)
  const [objective, setObjective] = useState("");
  const [objSuggested, setObjSuggested] = useState("");
  const [objPresets, setObjPresets] = useState({});
  const [objMatterType, setObjMatterType] = useState("");
  const [objSaving, setObjSaving] = useState(false);
  const [objSavedAt, setObjSavedAt] = useState(null);
  const [repStatus, setRepStatus] = useState("");
  const [repState, setRepState] = useState("");

  const loadObjective = async () => {
    try {
      const { data } = await api.get("/matters/objective");
      setObjective(data.objective || "");
      setObjSuggested(data.suggested || "");
      setObjPresets(data.presets || {});
      setObjMatterType(data.matter_type || "");
      setRepStatus(data.representation_status || "");
      setRepState(data.state || "");
    } catch (e) { console.error("load objective failed", e); }
  };

  const saveRepresentation = async (val) => {
    setRepStatus(val);
    try { await api.put("/matters/representation", { representation_status: val }); toast.success("Representation updated"); }
    catch (e) { toast.error("Could not update representation"); }
  };
  useEffect(() => { loadObjective(); }, [matterId]);

  useEffect(() => {
    let active = true;
    api.get("/evidence/archives", { params: matterId ? { matter_id: matterId } : {} })
      .then(({ data }) => { if (active) { setArchives(data.archives || []); setArchiveIds([]); } })
      .catch(() => {});
    return () => { active = false; };
  }, [matterId]);

  const saveObjective = async () => {
    setObjSaving(true);
    try {
      await api.put("/matters/objective", { objective });
      setObjSavedAt(Date.now());
      toast.success("Case objective saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save objective"); }
    finally { setObjSaving(false); }
  };

  useEffect(() => {
    api.get("/reports/presets").then(({ data }) => setPresets(data)).catch((e) => console.error("load presets failed", e));
  }, []);

  const applyPreset = (p) => {
    setReportType(p.report_type || "master");
    setLayoutMode(p.export_mode || "court_ready");
    setExportMode(p.include_original ? "full" : "judicial");
    setIncludeComms(p.include_comms !== false);
    toast.success(`Applied preset “${p.name}”`);
  };
  const savePreset = async () => {
    const name = presetName.trim();
    if (!name) { toast.message("Name your preset"); return; }
    try {
      const { data } = await api.post("/reports/presets", {
        name, report_type: reportType, export_mode: layoutMode,
        include_original: exportMode === "full", include_comms: includeComms,
      });
      setPresets((p) => [data, ...p]);
      setPresetName("");
      toast.success("Preset saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save preset"); }
  };
  const deletePreset = async (id) => {
    try { await api.delete(`/reports/presets/${id}`); setPresets((p) => p.filter((x) => x.id !== id)); }
    catch { toast.error("Could not delete preset"); }
  };
  const [packs, setPacks] = useState(null);
  const [stats, setStats] = useState(null);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/reports/jobs");
      const jobs = data.jobs || [];
      setHistory(jobs);
      // Resume an in-progress export if the user navigated away and came back
      if (!jobId && jobs.length && (jobs[0].status === "queued" || jobs[0].status === "running")) {
        setJob(jobs[0]);
        setJobId(jobs[0].job_id);
      }
    } catch (e) { console.warn("Reports: failed to load export history", e); }
  };

  const loadPacks = async () => {
    try { const { data } = await api.get("/billing/packs"); setPacks(data); } catch (e) { console.warn("Reports: failed to load billing packs", e); }
    try { const { data } = await api.get("/reports/export-stats"); setStats(data); } catch (e) { console.warn("Reports: failed to load export stats", e); }
  };
  // reload history + packs whenever the active case changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadPacks(); loadHistory(); }, [matterId]);

  const buyCasePrep = async () => {
    try {
      const { data } = await api.post("/billing/case-prep/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  useEffect(() => {
    // default select all
    setSelected(incidents.map((i) => i.id));
  }, [incidents]);

  // Poll the background export job for staged progress (no long-open request)
  useEffect(() => {
    if (!jobId) return;
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/reports/jobs/${jobId}`);
        if (!active) return;
        setJob(data);
        if (["completed", "failed", "stalled", "cancelled"].includes(data.status)) { loadHistory(); return; }
      } catch { /* keep polling */ }
      if (active) setTimeout(poll, 2000);
    };
    poll();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const jobActive = job && (job.status === "queued" || job.status === "running");

  const startExport = () => {
    if (!gate()) return;
    if (trialing) {
      openPaywall({
        feature: "Professional Export Required",
        reason: "A final, court-ready export (no watermark, clickable Table of Contents) requires Litigator Pro or a one-time Case Prep Pass for this case. You can still generate a watermarked preview.",
        unlock: "pro",
        altUnlock: "case_prep",
        proceedLabel: "Generate watermarked preview",
        onProceed: proceedExport,
      });
      return;
    }
    proceedExport();
  };
  const proceedExport = () => {
    setTrialGateOpen(false);
    if (!user?.has_vault_pin) { onSetupPin(); return; }
    setPin(""); setPinError(""); setPinOpen(true);
  };

  const doGenerate = async () => {
    if (pin.length !== 6) { setPinError("Enter your 6-digit PIN"); return; }
    setBusy(true); setPinError("");
    try {
      const { data } = await api.post("/reports/generate", {
        incident_ids: selected.length ? selected : null,
        case_number: caseNumber,
        export_mode: layoutMode,
        report_type: reportType,
        include_original: exportMode === "full",
        include_comms: includeComms,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        content_scope: contentScope,
        archive_ids: archiveIds,
        pin,
      });
      setPinOpen(false);
      setPin("");
      setJob(null);
      setJobId(data.job_id);
      toast.success(data.duplicate ? "Resuming your in-progress export" : "Export authorized — preparing your report");
      reloadAudit && reloadAudit();
    } catch (e) {
      setPinError(e.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const retryJob = async (id) => {
    try {
      const { data } = await api.post(`/reports/jobs/${id}/retry`);
      setJob(null);
      setJobId(data.job_id);
      toast.success("Retrying your export");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not retry export"); }
  };

  const cancelJob = async (id) => {
    try {
      await api.post(`/reports/jobs/${id}/cancel`);
      toast.success("Export cancelled");
      const { data } = await api.get(`/reports/jobs/${id}`);
      setJob(data);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not cancel export"); }
  };

  const refreshLink = async () => {
    if (!jobId) return;
    try { const { data } = await api.get(`/reports/jobs/${jobId}`); setJob(data); } catch (e) { console.warn("Reports: failed to refresh download link", e); }
  };

  return (
    <div className="space-y-6" data-testid="reports-view">
      <div>
        <div className="ek-stamp inline-block mb-3">COURT-READY REPORTS</div>
        <h2 className="font-head text-2xl font-semibold tracking-tight">Chain of custody export</h2>
        <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>
          Generate a professionally formatted PDF with SHA-256 integrity hashes, Bates numbering and a verification footer.
        </p>
      </div>

      <button onClick={openDigest} data-testid="preview-digest-btn" className="inline-flex items-center gap-2 text-sm underline min-h-[44px]" style={{ color: "var(--ek-primary)" }}>
        <Mail className="h-4 w-4" /> Preview my weekly case-health email
      </button>

      {!user.founding_member && packs && (
        packs.case_prep?.owned ? (
          <div className="border rounded-lg p-4 flex items-center gap-3" style={{ background: "rgba(16,185,129,0.08)", borderColor: "rgba(16,185,129,0.3)" }} data-testid="case-prep-active">
            <BadgeCheck className="h-5 w-5 shrink-0" style={{ color: "var(--ek-secure)" }} />
            <p className="text-sm" style={{ color: "var(--ek-text)" }}>
              <b>Case Prep Pass™ active.</b> Final court-ready exports for this case are unlocked — no watermark, clickable Table of Contents included.
            </p>
          </div>
        ) : trialing ? (
          <div className="border rounded-lg p-5" style={{ background: "rgba(46,130,246,0.07)", borderColor: "rgba(46,130,246,0.3)" }} data-testid="case-prep-cta">
            <div className="flex items-start gap-3">
              <Unlock className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "var(--ek-primary)" }} />
              <div className="flex-1">
                <p className="text-sm font-medium" style={{ color: "var(--ek-text)" }}>Preview mode — exports carry a PREVIEW watermark and the Table of Contents links are locked.</p>
                <p className="text-sm mt-1" style={{ color: "var(--ek-text-secondary)" }}>Unlock the final, court-ready PDF for <b>this case</b> — one-time Case Prep Pass™. Unlimited final exports, watermark removed, clickable Table of Contents.</p>
                {stats && (
                  <p className="ek-mono-label mt-2" data-testid="case-prep-stats">THIS CASE: {stats.record_count} RECORDS · {stats.exhibit_count} EXHIBITS · ~{stats.estimated_pages} PAGES · BATES {stats.bates_range}</p>
                )}
                <Button onClick={buyCasePrep} data-testid="buy-case-prep-btn" className="rounded-md mt-3" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                  <Unlock className="h-4 w-4 mr-2" /> Unlock final export — ${packs.case_prep?.price}
                </Button>
              </div>
            </div>
          </div>
        ) : null
      )}

      <Dialog open={digestOpen} onOpenChange={(v) => !v && setDigestOpen(false)}>
        <DialogContent className="sm:max-w-lg" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-semibold tracking-tight flex items-center gap-2"><Mail className="h-5 w-5" style={{ color: "var(--ek-primary)" }} /> Weekly digest preview</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>This is the Monday 8am email you'll receive summarizing your active cases.</DialogDescription>
          </DialogHeader>
          <iframe title="digest" srcDoc={digestHtml} data-testid="digest-preview-frame" className="w-full rounded-md" style={{ height: "60vh", border: "1px solid var(--ek-border)", background: "#070B14" }} />
        </DialogContent>
      </Dialog>

      {!user.has_vault_pin && (
        <div className="border rounded-lg p-5 flex items-start gap-3" style={{ background: "rgba(245,158,11,0.08)", borderColor: "rgba(245,158,11,0.25)" }} data-testid="reports-need-pin">
          <Lock className="h-5 w-5 mt-0.5" style={{ color: "var(--ek-warning)" }} />
          <div>
            <p className="text-sm" style={{ color: "var(--ek-text)" }}>A Vault PIN is required to authorize exports.</p>
            <button onClick={onSetupPin} data-testid="reports-setup-pin" className="text-sm underline mt-1" style={{ color: "var(--ek-warning)" }}>Set your Vault PIN</button>
          </div>
        </div>
      )}

      <div className="border rounded-lg p-5 space-y-5" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="space-y-2 max-w-xs flex-1">
            <Label htmlFor="case">Case number (optional)</Label>
            <Input id="case" data-testid="case-number-input" value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} className="bg-transparent" placeholder="e.g. EK-2026-0042" />
          </div>
          <Button onClick={() => gate(() => setShareOpen(true))} disabled={!matterId} data-testid="open-share-btn" variant="outline" className="rounded-md border self-end" style={{ borderColor: "rgba(46,130,246,0.4)", background: "transparent", color: "var(--ek-primary)" }}>
            <ShieldCheck className="h-4 w-4 mr-2" /> Secure Professional Review™
          </Button>
        </div>

        <div className="space-y-2">
          <Label>Records to include</Label>
          {incidents.length === 0 ? (
            <p className="text-sm" style={{ color: "var(--ek-text-muted)" }}>No records yet. Create incidents first.</p>
          ) : (
            <div className="border rounded-md divide-y max-h-64 overflow-y-auto" style={{ borderColor: "var(--ek-border)" }}>
              {incidents.map((inc) => (
                <label key={inc.id} className="flex items-center gap-3 p-3.5 min-h-[48px] cursor-pointer" data-testid={`report-record-${inc.id}`}>
                  <Checkbox checked={selected.includes(inc.id)} onCheckedChange={() => toggle(inc.id)} />
                  <span className="text-sm truncate flex-1" data-private>{inc.pure_fact}</span>
                  <span className="ek-stamp">{inc.entry_type || "FACTS"}</span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2" data-testid="export-mode-group">
          <Label>Export mode</Label>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { key: "judicial", title: "Judicial Summary", desc: "Prints only the Court-Admissible Fact Extraction for each incident." },
              { key: "full", title: "Full Legal Discovery", desc: "Prints both the Raw Contemporaneous Entry and the Fact Extraction." },
            ].map((m) => {
              const on = exportMode === m.key;
              return (
                <button key={m.key} type="button" onClick={() => setExportMode(m.key)} data-testid={`export-mode-${m.key}`}
                  className="text-left rounded-md border p-3 transition-colors min-h-[64px]"
                  style={{ background: on ? "rgba(46,130,246,0.10)" : "transparent", borderColor: on ? "var(--ek-primary)" : "var(--ek-border)" }}>
                  <div className="flex items-center gap-2">
                    <span className="h-3.5 w-3.5 rounded-full border shrink-0" style={{ borderColor: on ? "var(--ek-primary)" : "var(--ek-border)", background: on ? "var(--ek-primary)" : "transparent" }} />
                    <span className="text-sm font-medium" style={{ color: "var(--ek-text)" }}>{m.title}</span>
                  </div>
                  <p className="text-xs mt-1 pl-5.5" style={{ color: "var(--ek-text-secondary)" }}>{m.desc}</p>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-2" data-testid="report-presets-block">
          <Label>Presets</Label>
          {presets.length > 0 && (
            <div className="flex flex-wrap gap-2" data-testid="preset-chips">
              {presets.map((p) => (
                <span key={p.id} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
                  <button onClick={() => applyPreset(p)} data-testid={`preset-apply-${p.id}`} style={{ color: "var(--ek-primary)" }}>{p.name}</button>
                  <button onClick={() => deletePreset(p.id)} data-testid={`preset-delete-${p.id}`} className="opacity-60 hover:opacity-100"><XCircle className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input value={presetName} onChange={(e) => setPresetName(e.target.value)} placeholder="Save current setup as…" data-testid="preset-name-input" className="bg-transparent" />
            <Button type="button" variant="outline" onClick={savePreset} data-testid="preset-save-btn" className="rounded-md shrink-0" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>Save preset</Button>
          </div>
        </div>

        <div className="space-y-2" data-testid="report-type-block">
          <Label>Report type</Label>
          <select value={reportType} onChange={(e) => setReportType(e.target.value)} data-testid="report-type-select"
            className="w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)", color: "var(--ek-text)" }}>
            <option value="master">Master Case Report (everything)</option>
            <option value="focused">Focused Report</option>
            <option value="timeline">Timeline Report</option>
            <option value="communication">Communication Report</option>
            <option value="financial">Financial Report</option>
            <option value="consistency">Court-Order Consistency Report</option>
            <option value="index">Evidence Index</option>
            <option value="professional">Professional Review Package</option>
            <optgroup label="Justice Intelligence">
              <option value="objective">Objective-Driven Case Report</option>
              <option value="evidence_index">Organize My Evidence (GAL Index)</option>
              <option value="full_court_prep">Full Court Prep (Attorney/GAL Package)</option>
              <option value="discovery_certificate">Discovery Response Certificate</option>
            </optgroup>
            <optgroup label="Professional formats">
              <option value="calendar_log">Calendar Transmission Log</option>
              <option value="exhibit_record">Exhibit Record</option>
              <option value="clinical_summary">Clinical, Health & Behavioral Summary</option>
              <option value="fact_metrics">Fact Metrics Report</option>
              <option value="memo">Memorandum of Documented Facts</option>
            </optgroup>
          </select>
          <div className="mt-3">
            <label className="ek-mono-label block mb-1">INCLUDE</label>
            <select value={contentScope} onChange={(e) => setContentScope(e.target.value)} data-testid="content-scope-select"
              className="w-full rounded-md border px-3 py-2 text-sm" style={{ background: "var(--ek-page)", borderColor: "var(--ek-border)", color: "var(--ek-text)" }}>
              <option value="both">Report body + evidence appendix</option>
              <option value="body">Report body only (no appendix)</option>
              <option value="appendix">Evidence appendix only</option>
            </select>
          </div>
          <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>Only sections with real evidence are included — no empty sections.</p>
          {archives.length > 0 && (
            <div className="mt-3" data-testid="report-archive-picker">
              <label className="ek-mono-label block mb-1">INCLUDE IMPORTED ARCHIVES AS APPENDIX</label>
              <div className="space-y-1.5 border rounded-md p-2 max-h-44 overflow-y-auto" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
                {archives.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 p-1 cursor-pointer" data-testid={`report-archive-${a.id}`}>
                    <Checkbox
                      checked={archiveIds.includes(a.id)}
                      onCheckedChange={(v) => setArchiveIds((s) => v ? [...s, a.id] : s.filter((x) => x !== a.id))}
                      data-testid={`report-archive-check-${a.id}`}
                    />
                    <FolderArchive className="h-3.5 w-3.5 shrink-0" style={{ color: "var(--ek-primary)" }} />
                    <span className="text-xs truncate flex-1" data-private>{a.filename}</span>
                    <span className="ek-mono-label shrink-0">{a.child_count} file{a.child_count === 1 ? "" : "s"}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--ek-text-muted)" }}>Selected archives add every extracted file to the report's exhibit appendix (Bates-numbered).</p>
            </div>
          )}
        </div>

        <div className="space-y-2" data-testid="representation-block">
          <Label className="ek-mono-label">REPRESENTATION {repState ? `· ${repState}` : ""}</Label>
          <div className="flex gap-2">
            {[{ v: "pro_se", l: "Pro Se" }, { v: "attorney_represented", l: "Attorney-Represented" }].map((o) => (
              <button key={o.v} type="button" data-testid={`rep-${o.v}`} onClick={() => saveRepresentation(repStatus === o.v ? "" : o.v)}
                className="flex-1 rounded-md border px-3 py-2 text-sm transition-colors"
                style={{ borderColor: repStatus === o.v ? "var(--ek-primary)" : "var(--ek-border)", background: repStatus === o.v ? "rgba(46,130,246,0.08)" : "transparent", color: "var(--ek-text)" }}>
                {o.l}
              </button>
            ))}
          </div>
          <p className="text-xs" style={{ color: "var(--ek-text-muted)" }}>Adjusts report wording — Pro Se filers vs. counsel review. Shown on the report cover.</p>
        </div>

        {reportType === "objective" && (
          <div className="space-y-2 rounded-md border p-3" data-testid="objective-block"
            style={{ borderColor: "rgba(46,130,246,0.35)", background: "rgba(46,130,246,0.05)" }}>
            <Label className="ek-mono-label">CASE OBJECTIVE</Label>
            <p className="text-xs" style={{ color: "var(--ek-text-secondary)" }}>
              Justice organizes this report by your confirmed court-order provisions, listing the documented facts that
              relate to each. Set your case objective below.
            </p>
            <textarea value={objective} onChange={(e) => setObjective(e.target.value)} rows={3} data-testid="objective-input"
              placeholder={objSuggested || "e.g. Maintain a consistent, factual record of parenting time and exchanges."}
              className="w-full rounded-md border px-3 py-2 text-sm bg-transparent"
              style={{ borderColor: "var(--ek-border)", color: "var(--ek-text)" }} />
            {objSuggested && (
              <button type="button" data-testid="objective-preset-btn" onClick={() => setObjective(objSuggested)}
                className="text-xs underline" style={{ color: "var(--ek-primary)" }}>
                Use suggested objective for {objMatterType || "this case"}
              </button>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <Button type="button" variant="outline" onClick={saveObjective} disabled={objSaving} data-testid="objective-save-btn"
                className="rounded-md" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>
                {objSaving ? "Saving…" : "Save objective"}
              </Button>
              {objSavedAt && <span className="text-xs" style={{ color: "var(--ek-primary)" }}>Saved ✓</span>}
            </div>
            <p className="text-[11px]" style={{ color: "var(--ek-text-muted)" }}>
              This report uses KeeperCredits to map your evidence to each provision. Confirm provisions under Court Order first.
              Neutral, fact-only — it never determines compliance, fault, or legal outcome.
            </p>
          </div>
        )}

        <div className="space-y-2" data-testid="layout-mode-block">
          <Label>Layout</Label>
          <div className="grid grid-cols-2 gap-2">
            {[["court_ready", "Court Ready", "Full professional packet"], ["compile_review", "Evidence Compile Review", "Original ↔ AI-extracted, side by side"]].map(([k, l, d]) => (
              <button key={k} type="button" onClick={() => setLayoutMode(k)} data-testid={`layout-${k}`}
                className="rounded-md border p-3 text-left transition-colors"
                style={{ borderColor: layoutMode === k ? "var(--ek-primary)" : "var(--ek-border)", background: layoutMode === k ? "rgba(46,130,246,0.08)" : "transparent" }}>
                <div className="text-sm font-medium" style={{ color: layoutMode === k ? "var(--ek-primary)" : "var(--ek-text)" }}>{l}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--ek-text-muted)" }}>{d}</div>
              </button>
            ))}
          </div>
        </div>

        <button type="button" onClick={() => setIncludeComms((v) => !v)} data-testid="include-comms-toggle" aria-pressed={includeComms}
          className="w-full flex items-center gap-3 rounded-md border p-3 text-left transition-colors"
          style={{ borderColor: includeComms ? "var(--ek-primary)" : "var(--ek-border)", background: includeComms ? "rgba(46,130,246,0.08)" : "transparent" }}>
          <span className="h-5 w-9 rounded-full shrink-0 relative transition-colors" style={{ background: includeComms ? "var(--ek-primary)" : "var(--ek-border)" }}>
            <span className="absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all" style={{ left: includeComms ? "18px" : "2px" }} />
          </span>
          <span className="flex-1 min-w-0">
            <span className="text-sm font-medium block" style={{ color: "var(--ek-text)" }}>Include communication log</span>
            <span className="text-xs" style={{ color: "var(--ek-text-secondary)" }}>Adds a neutral summary + timestamped table of logged calls & texts (when the case has any).</span>
          </span>
        </button>

        <Button onClick={startExport} disabled={(incidents.length === 0 && archiveIds.length === 0) || jobActive} data-testid="export-pdf-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
          {jobActive ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>) : (<><FileBarChart2 className="h-4 w-4 mr-2" /> Export to PDF ({selected.length || incidents.length || archiveIds.length})</>)}
        </Button>
      </div>

      {job && (job.status === "queued" || job.status === "running") && (
        <div className="border rounded-lg p-5" style={{ background: "var(--ek-surface)", borderColor: "rgba(46,130,246,0.3)" }} data-testid="export-progress">
          <div className="flex flex-col items-center text-center mb-4">
            <JusticeThinking size={76} message={REPORT_STAGE_MESSAGES[job.stage] || "Justice is preparing your report…"} testid="report-justice-processing" />
            <div className="ek-mono-label mt-2">EXPORT JOB · {job.job_id}</div>
          </div>
          <div className="h-2 rounded-full overflow-hidden mb-4" style={{ background: "var(--ek-page)" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${job.percent || 0}%`, background: "var(--ek-primary)" }} data-testid="export-progress-bar" />
          </div>
          {job.stage === "pdf" && job.page_current > 0 && (
            <p className="text-sm mb-4 font-medium" style={{ color: "var(--ek-primary)" }} data-testid="export-page-eta">
              Rendering page {job.page_current}{job.page_total ? ` of ${job.page_total}` : ""}
              {job.eta_seconds != null && job.eta_seconds >= 0 ? ` · about ${job.eta_seconds < 60 ? `${job.eta_seconds}s` : `${Math.ceil(job.eta_seconds / 60)} min`} remaining` : ""}
            </p>
          )}
          <div className="space-y-2">
            {(job.checklist || []).map((s) => (
              <div key={s.key} className="flex items-center gap-2.5 text-sm" data-testid={`stage-${s.key}`}>
                {s.state === "done" ? <CheckCircle2 className="h-4 w-4" style={{ color: "var(--ek-secure)" }} />
                  : s.state === "current" ? <Loader2 className="h-4 w-4 animate-spin" style={{ color: "var(--ek-primary)" }} />
                  : <Circle className="h-4 w-4" style={{ color: "var(--ek-text-muted)" }} />}
                <span style={{ color: s.state === "pending" ? "var(--ek-text-muted)" : "var(--ek-text)" }}>{s.label}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mt-4 gap-3 flex-wrap">
            <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>{job.percent || 0}% · you can leave this screen — it'll be in Export History.</p>
            <button onClick={() => cancelJob(job.job_id)} data-testid="cancel-export-btn" className="text-sm underline" style={{ color: "var(--ek-text-secondary)" }}>Cancel export</button>
          </div>
        </div>
      )}

      {job && job.status === "completed" && (
        <div className="border rounded-lg p-5" style={{ background: "var(--ek-surface)", borderColor: "rgba(16,185,129,0.3)" }} data-testid="report-result">
          <div className="flex items-center gap-2 mb-3">
            <ShieldCheck className="h-5 w-5" style={{ color: "var(--ek-secure)" }} />
            <span className="font-head font-semibold tracking-tight">Your report is ready</span>
          </div>
          <div className="ek-mono-label mb-1">REPORT ID · {job.job_id}</div>
          <div className="ek-mono-label mb-1">SHA-256 · {job.sha256?.slice(0, 24)}…</div>
          <div className="ek-mono-label mb-4">{job.record_count} RECORD(S){typeof job.exhibit_count === "number" ? ` · ${job.exhibit_count} EXHIBIT(S)` : ""}</div>
          <div className="flex items-center gap-3 flex-wrap">
            <a href={`${BACKEND_URL}${job.download_url}`} download data-testid="download-report-btn">
              <Button className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
                <Download className="h-4 w-4 mr-2" /> Download PDF
              </Button>
            </a>
            <a href={`${BACKEND_URL}${job.download_url}`} target="_blank" rel="noreferrer" data-testid="view-report-btn">
              <Button variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>View report</Button>
            </a>
            <button onClick={refreshLink} data-testid="refresh-link-btn" className="text-sm underline" style={{ color: "var(--ek-text-secondary)" }}>Refresh link</button>
          </div>
        </div>
      )}

      {job && (job.status === "failed" || job.status === "stalled" || job.status === "cancelled") && (
        <div className="border rounded-lg p-5" style={{ background: "var(--ek-surface)", borderColor: "rgba(240,97,109,0.35)" }} data-testid="export-failed">
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="h-5 w-5" style={{ color: "var(--ek-error)" }} />
            <span className="font-head font-semibold tracking-tight">
              {job.status === "cancelled" ? "Export cancelled" : job.status === "stalled" ? "Export stalled" : "We couldn't finish this report"}
            </span>
          </div>
          <p className="text-sm" style={{ color: "var(--ek-text-secondary)" }}>Your evidence and original files were not changed.</p>
          <div className="ek-mono-label mt-2 mb-1">EXPORT JOB · {job.job_id}</div>
          <p className="text-sm mb-4" style={{ color: "var(--ek-text)" }}>{job.error || "This export did not complete."}</p>
          {(job.error_type || job.error_detail || job.error_stage) && (
            <details className="mb-4" data-testid="export-error-details">
              <summary className="text-xs cursor-pointer" style={{ color: "var(--ek-text-secondary)" }}>View error details</summary>
              <div className="ek-mono-label mt-2 space-y-1 rounded-md border p-2.5" style={{ borderColor: "var(--ek-border)", background: "var(--ek-page)" }}>
                {job.error_stage && <div>STAGE · {job.error_stage}</div>}
                {job.error_type && <div>TYPE · {job.error_type}</div>}
                {job.error_detail && <div className="break-words" style={{ whiteSpace: "normal" }}>DETAIL · {job.error_detail}</div>}
                <div>JOB · {job.job_id}</div>
              </div>
            </details>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <Button onClick={() => retryJob(job.job_id)} data-testid="retry-export-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              <RotateCcw className="h-4 w-4 mr-2" /> {job.status === "stalled" ? "Resume export" : "Retry export"}
            </Button>
            <a href="mailto:support@herrellinteractive.com" className="text-sm underline" style={{ color: "var(--ek-text-secondary)" }}>Contact support</a>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="border rounded-lg p-5" style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="export-history">
          <div className="flex items-center gap-2 mb-3">
            <History className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
            <span className="font-head font-semibold tracking-tight">Export history</span>
          </div>
          <div className="divide-y" style={{ borderColor: "var(--ek-border)" }}>
            {history.map((h) => (
              <div key={h.job_id} className="flex items-center justify-between gap-3 py-3 flex-wrap" data-testid={`history-${h.job_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: "var(--ek-text)" }}>
                    {(REPORT_LABELS[h.report_type] || "Report")}{h.export_mode === "compile_review" ? " · Compile Review" : (h.export_mode === "court_ready" || h.export_mode === "judicial" || h.export_mode === "full" ? " · Court Ready" : "")}{h.record_count != null ? ` · ${h.record_count} records` : ""}
                  </div>
                  <div className="ek-mono-label mt-0.5">{h.job_id} · {(h.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <div className="flex items-center gap-3">
                  {h.stale && (
                    <span className="ek-stamp inline-flex items-center gap-1" data-testid={`history-stale-${h.job_id}`} style={{ color: "var(--ek-warning)", borderColor: "rgba(245,158,11,0.4)" }} title={h.stale_reason || "May reference deleted evidence — regenerate"}>
                      <AlertTriangle className="h-3 w-3" /> REGENERATE
                    </span>
                  )}
                  <span className="ek-stamp" style={{ color: h.status === "completed" ? "var(--ek-secure)" : h.status === "failed" ? "var(--ek-error)" : "var(--ek-primary)" }}>{h.status.toUpperCase()}</span>
                  {h.status === "completed" && h.download_url && (
                    <a href={`${BACKEND_URL}${h.download_url}`} download data-testid={`history-download-${h.job_id}`}>
                      <Button size="sm" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}><Download className="h-3.5 w-3.5" /></Button>
                    </a>
                  )}
                  {h.status === "failed" && (
                    <Button size="sm" onClick={() => retryJob(h.job_id)} data-testid={`history-retry-${h.job_id}`} variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}><RotateCcw className="h-3.5 w-3.5" /></Button>
                  )}
                  {h.status !== "running" && (
                    <Button size="sm" onClick={() => setDelJob(h)} data-testid={`history-delete-${h.job_id}`} variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent", color: "var(--ek-error)" }}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={delJob != null} onOpenChange={(v) => !v && !delBusy && setDelJob(null)}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="delete-report-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-semibold flex items-center gap-2" style={{ color: "var(--ek-error)" }}>
              <Trash2 className="h-5 w-5" /> Delete this report?
            </DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              This permanently removes the generated PDF. It cannot be recovered — you can always regenerate a fresh report from your evidence. Your evidence and records are not affected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-col gap-2">
            <Button onClick={deleteReport} disabled={delBusy} data-testid="confirm-delete-report-btn" className="w-full rounded-md" style={{ background: "var(--ek-error)", color: "#fff" }}>
              {delBusy ? "Deleting…" : "Delete report permanently"}
            </Button>
            <Button onClick={() => setDelJob(null)} disabled={delBusy} variant="ghost" data-testid="cancel-delete-report-btn" className="w-full rounded-md" style={{ color: "var(--ek-text-muted)" }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }}>
          <DialogHeader>
            <div className="flex items-center gap-2 mb-1">
              <Lock className="h-5 w-5" style={{ color: "var(--ek-primary)" }} />
              <DialogTitle className="font-head font-semibold tracking-tight">Authorize export</DialogTitle>
            </div>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              Enter your 6-digit Vault PIN to generate and sign this report.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <PinPad value={pin} onChange={setPin} testid="export-pin-input" autoFocus />
            {pinError && <p className="text-sm" style={{ color: "var(--ek-error)" }}>{pinError}</p>}
          </div>
          <DialogFooter>
            <Button onClick={doGenerate} disabled={busy} data-testid="confirm-export-btn" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>
              {busy ? "GENERATING..." : "Authorize & generate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShareModal open={shareOpen} onOpenChange={setShareOpen} matterId={matterId} />

      <Dialog open={trialGateOpen} onOpenChange={setTrialGateOpen}>
        <DialogContent style={{ background: "var(--ek-surface)", borderColor: "var(--ek-border)" }} data-testid="trial-gate-dialog">
          <DialogHeader>
            <DialogTitle className="font-head font-semibold tracking-tight">Start your subscription?</DialogTitle>
            <DialogDescription style={{ color: "var(--ek-text-secondary)" }}>
              Generating this professional report will end your free trial and start your subscription. Proceed?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setTrialGateOpen(false)} data-testid="trial-gate-cancel" variant="outline" className="rounded-md border" style={{ borderColor: "var(--ek-border)", background: "transparent" }}>Not yet</Button>
            <Button onClick={proceedExport} data-testid="trial-gate-proceed" className="rounded-md" style={{ background: "var(--ek-primary)", color: "#fff" }}>Proceed</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
